/**
 * Career logging for M7 (spec §12.1 "logging now", §9; plan S1). Pure Career-in, Career-out commands; none is a
 * hot path (one call per sale, hand-in or kill).
 *
 * - **Tetherstone sales** (`recordTetherSale`): the D-340 bounded sale ring (`career/logs.ts`, 64 entries) plus
 *   running totals in `career.tetherTotals` (tonnes, credits, sales), which never drop old sales.
 * - **Guardian kills** (`recordGuardianKill`): the D-340 bounded kill ring (32 entries). The worker queues each kill
 *   (`outlaws/guardian.ts recordPlayerKill`, with its hull, pilot seed and self-defence flag) and the Charter
 *   career bridge appends it; the Holdfast cost is the worker's (D-338, D-339), never applied here.
 * - **Income mix** (`recordIncome`): credits by source in `logging.json incomeBuckets` rolling buckets of
 *   `incomeBucketS` game seconds (10 × 30 min ≈ 5 h of play), oldest first. `dominantIncome` names every source
 *   over `dominantShare` of the window's income (the §9 owned-pilot retaliation rows).
 */
import loggingJson from '../data/story/logging.json'
import { careerDay, withGuardianKills, withTetherSale } from '../career/logs'
import type { Career, GuardianKillEntry } from '../career/types'
import type { ContractType, StationId } from '../data/m2Schemas'
import { loggingFileSchema, type LoggingConfig } from './schemas'
import { INCOME_SOURCES, type IncomeBucket, type IncomeMix, type IncomeSource, type TetherTotals } from './types'

export const LOGGING: LoggingConfig = loggingFileSchema.parse(loggingJson)

/** A tetherstone sale as the trade command sees it (the day is the Career clock's). */
export interface TetherSaleInput {
  stationId: StationId
  tonnes: number
  credits: number
  /** First Rush bunker tetherstone from a restarted ore processor (spec §16.9). */
  bunker?: boolean
}

const NO_TOTALS: TetherTotals = { tonnes: 0, credits: 0, sales: 0 }

/** The Career's tetherstone running totals (zero when none logged). */
export function tetherTotalsOf(career: Career): TetherTotals {
  return career.tetherTotals ?? NO_TOTALS
}

/** The Career with one tetherstone sale in the bounded sale log and the running totals. */
export function recordTetherSale(career: Career, sale: TetherSaleInput): Career {
  const entry = { day: careerDay(career), stationId: sale.stationId, tonnes: sale.tonnes, credits: sale.credits }
  const logged = withTetherSale(career, sale.bunker === true ? { ...entry, bunker: true } : entry)
  const t = tetherTotalsOf(career)
  return { ...logged, tetherTotals: { tonnes: t.tonnes + sale.tonnes, credits: t.credits + sale.credits, sales: t.sales + 1 } }
}

/** The Career with one guardian kill in the bounded kill log (the M7 fields are kept when present). */
export function recordGuardianKill(career: Career, kill: GuardianKillEntry): Career {
  return withGuardianKills(career, [kill])
}

function emptyBucket(): IncomeBucket {
  return { mining: 0, combat: 0, salvage: 0, trade: 0, contracts: 0 }
}

/** The start of the bucket holding game time `t`. */
function bucketStartOf(t: number, cfg: LoggingConfig): number {
  return Math.floor(t / cfg.incomeBucketS) * cfg.incomeBucketS
}

/**
 * The mix rolled forward to game time `now`: empty buckets for the time passed, only the last `incomeBuckets` kept.
 * Time running backwards (it doesn't: `gameTime` only grows) keeps the mix as it is.
 */
export function rollIncomeMix(mix: IncomeMix | undefined, now: number, cfg: LoggingConfig = LOGGING): IncomeMix {
  const start = bucketStartOf(now, cfg)
  if (mix === undefined || mix.buckets.length === 0) return { bucketStart: start, buckets: [emptyBucket()] }
  const steps = Math.round((start - mix.bucketStart) / cfg.incomeBucketS)
  if (steps <= 0) return mix
  if (steps >= cfg.incomeBuckets) return { bucketStart: start, buckets: [emptyBucket()] }
  const buckets = mix.buckets.slice()
  for (let i = 0; i < steps; i++) buckets.push(emptyBucket())
  return { bucketStart: start, buckets: buckets.slice(Math.max(0, buckets.length - cfg.incomeBuckets)) }
}

/** The Career with `credits` of income from `source` in the current bucket (at `career.gameTime`). */
export function recordIncome(career: Career, source: IncomeSource, credits: number, cfg: LoggingConfig = LOGGING): Career {
  if (!(credits > 0) || !Number.isFinite(credits)) return career
  const mix = rollIncomeMix(career.incomeMix, career.gameTime, cfg)
  const last = mix.buckets.length - 1
  const newest = mix.buckets[last]
  const buckets = mix.buckets.slice()
  buckets[last] = { ...newest, [source]: newest[source] + credits }
  return { ...career, incomeMix: { bucketStart: mix.bucketStart, buckets } }
}

/** Credits by source over the window ending at `now` (default the Career's game time). */
export function incomeTotals(career: Career, now: number = career.gameTime, cfg: LoggingConfig = LOGGING): IncomeBucket {
  const out = emptyBucket()
  if (career.incomeMix === undefined) return out
  const mix = rollIncomeMix(career.incomeMix, now, cfg)
  for (const b of mix.buckets) for (const s of INCOME_SOURCES) out[s] += b[s]
  return out
}

/**
 * Every source over `dominantShare` of the window's income (spec §9 "every source over ~25% gets its row"), in
 * `INCOME_SOURCES` order. Empty when the window holds no income.
 */
export function dominantIncome(career: Career, now: number = career.gameTime, cfg: LoggingConfig = LOGGING): IncomeSource[] {
  const totals = incomeTotals(career, now, cfg)
  let sum = 0
  for (const s of INCOME_SOURCES) sum += totals[s]
  if (sum <= 0) return []
  return INCOME_SOURCES.filter((s) => totals[s] > cfg.dominantShare * sum)
}

/** The income source a contract hand-in books to (`logging.json combatContractTypes` → combat, else contracts). */
export function contractIncomeSource(type: ContractType, cfg: LoggingConfig = LOGGING): IncomeSource {
  return cfg.combatContractTypes.includes(type) ? 'combat' : 'contracts'
}
